import { AfterViewInit, Component, EventEmitter, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {merge, Observable, of as observableOf, of, Subject, throwError} from 'rxjs';
import {catchError, debounceTime, delay, first, map, startWith, switchMap, tap} from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { AuthorizeService } from '../../api-authorization/authorize.service';
import { ILabelRow, LabelFilters, LabelsService } from '../labels.service';
import { ISupplier, SupplierSection, SupplierService } from '../supplier.service';
import { Role } from '../_models/role';
import { FormControl } from '@angular/forms';
import { UserService } from '../user.service';
import { DatePipe } from '@angular/common';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { AutocompleteService, IFust } from '../autocomplete.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { SharedNotificationService } from '../shared-notification.service';

@Component({
    selector: 'app-labels',
    templateUrl: './labels.component.html',
    styleUrls: ['./labels.component.css'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
    ],
})
export class LabelsComponent implements OnInit, AfterViewInit {
    _baseUrl: string;
    isAdmin: Observable<boolean>;

    searchTermChanged = new Subject<string>();

    data: ILabelRow[] = [];
    filteredData: ILabelRow[];

    control: FormControl = new FormControl('');
    suppliers: ISupplier[] = [];
    filteredSuppliers: Observable<ISupplier[]>;

    supplierAssignedIdcontrol: FormControl = new FormControl('');
    kwekerartikelnrs: string[] = [];
    filteredSupplierAssignedIds: Observable<string[]>;

    potmaatcontrol: FormControl = new FormControl('');
    potmaten: number[] = [];
    filteredPotmaten: Observable<string[]>;

    afmetingStickercontrol: FormControl = new FormControl('');
    afmetingStickerOptions: string[] = [];
    filteredAfmetingStickers: Observable<string[]>;

    selectedEan: number;
    
    printedPageIndex = 0;
    printedPageSize = 100;
    printedTotalCount = 0;
    dataPrinted: ILabelRow[] = [];

    unprintedPageIndex = 0;
    unprintedPageSize = 100;
    unprintedTotalCount = 0;
    dataUnPrinted: ILabelRow[] = [];

    isLoadingRowResults = true;
    isLoadingSupplierResults = true;

    numberOfLabels: number = 0;
    isAllPrinted: boolean;
    saveButtonIsEnabled: boolean;
    filters: LabelFilters;
    selectedWeergave: number = 0;

    labelsFilter: EventEmitter<any> = new EventEmitter();

    @ViewChild(MatSort, { static: false }) sort: MatSort;
    selectedTabIndex: number = 0;
    unprintedSelection: ILabelRow[] = [];
    printedSelection: ILabelRow[] = [];

    constructor(
        private _notificationService: SharedNotificationService,
        private authorizeService: AuthorizeService,
        private _supplierService: SupplierService,
        private _autocompleteService: AutocompleteService,
        private _labelsService: LabelsService,
        private _userService: UserService,
        @Inject('BASE_URL') baseUrl: string) {
        this._baseUrl = baseUrl;
    }

    ngOnInit() {
        this.isAdmin = this.authorizeService.getUser().pipe(map(u => u && u.role && (u.role.includes(Role.Developer) || u.role.includes(Role.Administrator))));
        this.isAdmin.subscribe(data => data ? this.control.enable() : this.control.disable());

        this.filteredSuppliers = this.control.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || '')),
        );
        this._supplierService.getSuppliers(SupplierSection.labels).subscribe(data => {
            this.suppliers = data.items;

            var selectedSupplier = this.suppliers.find(function (element) {
                return element.selected;
            });
            if (selectedSupplier) {
                this.selectedEan = selectedSupplier.ean;
                this.control.patchValue(selectedSupplier.name);

                this.setAutocompletes();
            }

            this.isLoadingSupplierResults = false;
        });

        this.filters = new LabelFilters();

        this.filteredSupplierAssignedIds = this.supplierAssignedIdcontrol.valueChanges.pipe(
            startWith(''),
            map(value => this._filterSupplierAssignedIds(value || '')),
        );

        this.filteredPotmaten = this.potmaatcontrol.valueChanges.pipe(
            startWith(''),
            map(value => this._filterPotmaten(value || '')),
        );

        this.filteredAfmetingStickers = this.afmetingStickercontrol.valueChanges.pipe(
            startWith(''),
            map(value => this._filterAfmetingStickers(value || '')),
        );

        this.searchTermChanged.pipe(
            debounceTime(2000)
        ).subscribe(searchTerm => {
            this.fetchUnprintedLabels(this.printedPageIndex, this.printedPageSize);
            this.fetchPrintedLabels(this.unprintedPageIndex, this.unprintedPageSize);
        });

    }

    ngAfterViewInit() {
        this.fetchUnprintedLabels();
        this.fetchPrintedLabels();
    }

    fetchUnprintedLabels(pageIndex = this.printedPageIndex, pageSize = this.printedPageSize) {
        this.isLoadingRowResults = true;
        this._labelsService.getLabelsForSupplier(
            0,
            false,
            this.filters,
            this.selectedEan,
            pageSize,
            pageIndex + 1
        ).subscribe(result => {
            this.dataPrinted = result.items;
            this.printedTotalCount = result.totalCount;
            this.printedPageSize = result.pageSize;
            this.printedPageIndex = result.pageNumber - 1;
            this.isLoadingRowResults = false;
        });
    }

    fetchPrintedLabels(pageIndex = this.unprintedPageIndex, pageSize = this.unprintedPageSize) {
        this.isLoadingRowResults = true;
        this._labelsService.getLabelsForSupplier(
            0,
            true,
            this.filters,
            this.selectedEan,
            pageSize,
            pageIndex + 1
        ).subscribe(result => {
            this.dataUnPrinted = result.items;
            this.unprintedTotalCount = result.totalCount;
            this.unprintedPageSize = result.pageSize;
            this.unprintedPageIndex = result.pageNumber - 1;
            this.isLoadingRowResults = false;
        });
    }

    onPrintedPageChanged(event) {
        this.fetchUnprintedLabels(event.pageIndex, event.pageSize);
    }
    
    onUnprintedPageChanged(event) {
        this.fetchPrintedLabels(event.pageIndex, event.pageSize);
    }

    setAutocompletes() {
        this.kwekerartikelnrs = [];
        this.potmaten = [];
        this.afmetingStickerOptions = [];

        this._autocompleteService.getKwekerArtikelNrs('', this.selectedEan).subscribe((data: string[]) => {
            if (data.length) {
                this.kwekerartikelnrs = data;
            }
            this.supplierAssignedIdcontrol.enable();
        });

        this._autocompleteService.getPotmaten('', this.selectedEan).subscribe((data: number[]) => {
            if (data.length) {
                this.potmaten = data;
            }
            this.potmaatcontrol.enable();
        });

        this._autocompleteService.getAfmetingStickers('', this.selectedEan).subscribe((data: string[]) => {
            if (data.length) {
                this.afmetingStickerOptions = data;
            }
            this.afmetingStickercontrol.enable();
        });
    }

    onSearchTermInput(value: string) {
        this.searchTermChanged.next(value);
    }

    private _filterPotmaten(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.potmaten.filter(k => this._normalizeValue(k.toString()).includes(filterValue)).map(function (e) { return e.toString() });
    }

    private _filterAfmetingStickers(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.afmetingStickerOptions.filter(k => this._normalizeValue(k).includes(filterValue));
    }

    private _filterSupplierAssignedIds(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.kwekerartikelnrs.filter(k => this._normalizeValue(k).includes(filterValue));
    }

    private _filter(value: string): ISupplier[] {
        const filterValue = this._normalizeValue(value);
        return this.suppliers.filter(supplier => this._normalizeValue(supplier.name).includes(filterValue) || supplier.ean.toString() == filterValue);
    }

    private _normalizeValue(value: string): string {
        return value.toLowerCase().replace(/\s/g, '');
    }

    printPrintedLabels() {
        this.openPdf(this.printedSelection.map(r => r.orderNr));
        this.printedSelection = [];

        this._notificationService.setOnPrintPrintedLabels(true);
    }

    printUnPrintedLabels() {
        this.openPdf(this.unprintedSelection.map(r => r.orderNr));
        this.unprintedSelection = [];

        this._notificationService.setOnPrintUnprintedLabels(true);
    }

    markAsUnprinted() {
        this._labelsService.markAsUnprinted(this.printedSelection.map(r => r.orderNr)).subscribe(
            () => {
                this.printedSelection = [];
                this.refresh();

                this._notificationService.setOnPrintUnprintedLabels(true);
            }
        );
    }

    openPdf(orderNrs: string[]) {
        let currentUser: any;
        combineLatest(
            this._userService.getCurrentUser().pipe(
                tap(userResult => currentUser = userResult)
            ),
            this._labelsService.downloadPDF(orderNrs)
        ).pipe(
            first(),//makes sure the subscribtion get cleaned after first result
            tap(([user, fileResponse]) => {
                var fileURL = window.URL.createObjectURL(fileResponse);
                var newWindow = window.open(fileURL);
                if (newWindow) {
                    newWindow.focus();
                    newWindow.print();
                }
                this.refresh();
            }),
        ).subscribe();
    }

    refresh() {
        this.saveButtonIsEnabled = false;
        this.labelsFilter.emit();
        this.onSearchTermInput('');
    }

    onEnterSupplierAssignedId(evt: any) {
        const selectedSupplierAssignedId = this.kwekerartikelnrs.find(k =>
            k.toLowerCase() == evt.source.value.toLowerCase());
        if (evt.source.selected) {
            if (selectedSupplierAssignedId) {
                this.filters.kwekerartikelnr = selectedSupplierAssignedId;
                this.supplierAssignedIdcontrol.patchValue(selectedSupplierAssignedId, { emitEvent: false });

                this.refresh();
            }
        }
    }

    onEnterPotmaat(evt: any) {
        const selectedPotmaat = this.potmaten.find(k => k == evt.source.value);
        if (evt.source.selected) {
            if (selectedPotmaat) {
                this.filters.potmaat = selectedPotmaat.toString();
                this.potmaatcontrol.patchValue(selectedPotmaat, { emitEvent: false });

                this.refresh();
            }
        }
    }

    onEnterAfmetingSticker(evt: any) {
        const selectedAfmetingSticker = this.afmetingStickerOptions.find(k => k == evt.source.value);
        if (evt.source.selected) {
            if (selectedAfmetingSticker) {
                this.filters.afmetingSticker = selectedAfmetingSticker;
                this.afmetingStickercontrol.patchValue(selectedAfmetingSticker, { emitEvent: false });

                this.refresh();
            }
        }
    }

    onEnter(evt: any) {
        const selectedSupplier = this.suppliers.find(supplier =>
            supplier.name.toLowerCase() == evt.source.value.toLowerCase());
        if (evt.source.selected) {
            // set value to control because we just resetted the control
            //this.control.patchValue(evt.source.value);

            if (selectedSupplier) {
                this.selectedEan = selectedSupplier.ean;
                this.control.patchValue(selectedSupplier.name);

                this.setAutocompletes();

                this.doResetFilters(false);

                this.refresh();
            }
        }
    }

    doResetFilters(withRefresh: boolean) {
        this.filters = new LabelFilters();

        this.potmaatcontrol.patchValue(null);
        this.supplierAssignedIdcontrol.patchValue(null);
        this.afmetingStickercontrol.patchValue(null);

        if (withRefresh) {
            this.refresh();
        }
    }

    doResetSearchTerm() {
        this.filters.searchTerm = '';
        this.refresh();
    }

    onTabChange(event: MatTabChangeEvent) {
        this.selectedTabIndex = event.index;
    }

    onSelectionChangeUnprinted(selectedItems: ILabelRow[]) {
        this.unprintedSelection = selectedItems;
    }

    onSelectionChangePrinted(selectedItems: ILabelRow[]) {
        this.printedSelection = selectedItems;
    }
}